import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as ForPersonIcon } from '../../../../../assets/icons/sharing/forPerson.svg';
import { ReactComponent as FromPersonIcon } from '../../../../../assets/icons/sharing/fromPerson.svg';
import { ReactComponent as SharedByIcon } from '../../../../../assets/icons/sharing/sharedBy.svg';
import CopyActionButton from '../../../../../components/designe/ActionButtons/CopyActionButton';
import DeleteActionButton from '../../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../../components/designe/ActionButtons/EditActionButton';
import FinishActionButton from '../../../../../components/designe/ActionButtons/FinishActionButton';
import ShareActionButton from '../../../../../components/designe/ActionButtons/ShareActionButton';
import StartActionButton from '../../../../../components/designe/ActionButtons/StartActionButton';
import AppUserAvatar from '../../../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppDialog from '../../../../../components/reusable/app-dialog/AppDialog';
import ConfirmDialog from '../../../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useNavigationStack } from '../../../../../context/NavigationStackContext';
import { useRestrictions } from '../../../../../hooks/use-restrictions';
import {
  useCopyTraining,
  useDeleteTraining,
  useStartTraining,
} from '../../../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../../../redux/api/users/usersApi';
import {
  AvatarSize,
  AvatarTypes,
  TrainingStatus,
} from '../../../../../shared/constants';
import { Paths } from '../../../../../shared/paths';
import { ITraining } from '../../../../../types/interfaces/training';
import { getUserViewName } from '../../../../../utils/common/shared-util';
import { getTrainingDate } from '../../../../../utils/datetime/date-time-utils';
import { useStyles } from './TrainingListItem.styles';
import CopyTraining from './copy-training/CopyTraining';
import ShareTraining from './share-training/ShareTraining';
import {
  TrainingListKind,
  getTrainingListSubtitle,
} from './training-list-subtitle';

interface ITrainingListItemProps {
  training: ITraining;
  showStartButton?: boolean;
  onStartRestricted?: () => void;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showCopyButton?: boolean;
  showShareButton?: boolean;
  showAvatar: boolean;
  onTrainingDeleted?: () => Promise<void> | void;
  sharedNavigation?: boolean;
  listKind?: TrainingListKind;
}

const TrainingListItem = (props: ITrainingListItemProps) => {
  const {
    training,
    showStartButton = true,
    onStartRestricted,
    showAvatar,
    showEditButton = true,
    showDeleteButton = true,
    showCopyButton = true,
    showShareButton = true,
    onTrainingDeleted,
    sharedNavigation = false,
    listKind = 'my',
  } = props;
  const { t } = useTranslation();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [startTrainingDialogMode, setStartTrainingDialogMode] = useState<
    'default' | 'another' | null
  >(null);
  const [pendingStartId, setPendingStartId] = useState<string | null>(null);
  const [showCopyTrainingDialog, setShowCopyTrainingDialog] = useState(false);
  const [startTraining, { isLoading: isStartingTraining }] = useStartTraining();
  const [deleteTraining, { isLoading: isDeletingTraining }] =
    useDeleteTraining();
  const [copyTraining] = useCopyTraining();
  const { navigate } = useNavigationStack();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { data: user } = useGetMyDetails();
  const { isShowPlanDialogOnClick } = useRestrictions();
  const [isShareTraining, setIsShareTraining] = useState(false);

  const trainingDate = useMemo(() => {
    return getTrainingDate(training);
  }, [training]);

  const listSubtitle = useMemo(
    () => getTrainingListSubtitle(training, user, listKind),
    [training, user, listKind],
  );

  const SubtitleIcon =
    listSubtitle?.icon === 'forPerson'
      ? ForPersonIcon
      : listSubtitle?.icon === 'sharedBy'
        ? SharedByIcon
        : FromPersonIcon;

  const showTrainingActionButtons = useMemo(() => {
    const p = training.permissions;
    if (!p) {
      return false;
    }
    return (
      (showStartButton && (p.canStart || p.canFinish)) ||
      p.canCopy ||
      p.canShare ||
      p.canEdit ||
      p.canEditInfo ||
      p.canDelete
    );
  }, [training.permissions, showStartButton]);

  function onItemClick(training: ITraining) {
    if (sharedNavigation) {
      if (training.status === TrainingStatus.finished) {
        navigate(Paths.trainings.toFinished(training.id));
      } else {
        navigate(Paths.trainings.toViewId(training.id));
      }
      return;
    }

    const p = training.permissions;
    if (training.status === TrainingStatus.notStarted) {
      navigate(Paths.trainings.toViewId(training.id));
      return;
    }
    if (training.status === TrainingStatus.finished) {
      if (p?.canEdit || p?.canEditInfo || p?.canCopy) {
        navigate(Paths.trainings.toFinished(training.id));
      } else {
        navigate(Paths.trainings.toViewId(training.id));
      }
      return;
    }
    if (training.status === TrainingStatus.started) {
      if (p?.canFinish || p?.canOpenLikeCurrent) {
        navigate(Paths.trainings.toCurrent(training.id));
      } else {
        navigate(Paths.trainings.toViewId(training.id));
      }
      return;
    }
    navigate(Paths.trainings.toViewId(training.id));
  }

  function onCopyTraining(data: {
    title: string;
    planedStartDate?: Date | null;
  }) {
    return copyTraining({
      trainingId: training.id,
      title: data.title,
      planedStartDate: data.planedStartDate,
    })
      .unwrap()
      .then((data) => {
        setShowCopyTrainingDialog(false);
        navigate(Paths.trainings.toViewId(data.id));
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function onEditClick(training: ITraining) {
    if (training.status === TrainingStatus.started) {
      navigate(Paths.trainings.toCurrent(training.id, true));
    } else if (training.status === TrainingStatus.finished) {
      navigate(Paths.trainings.toFinished(training.id, true));
    } else {
      navigate(Paths.trainings.toEditId(training.id));
    }
  }

  return (
    <React.Fragment>
      <div
        className={classes.trainingItemWrapper}
        onClick={() => onItemClick(training)}>
        <div className={classes.trainingItemHeader}>
          <Box
            display="flex"
            gap={1}
            width="100%"
            alignItems="flex-start"
            minWidth={0}>
            {showAvatar && (
              <AppUserAvatar
                entity={training.owner}
                size={AvatarSize.m}
                type={AvatarTypes.round}
              />
            )}
            <Box className={classes.trainingContentColumn}>
              <Box className={classes.trainingTitleRow}>
                <Box className={classes.trainingTitleColumn}>
                  <Typography variant="h3">
                    {training.permissions?.showPerformerNameInListTitle &&
                      getUserViewName(training.owner) + ' - '}
                    {training.title}
                  </Typography>
                  {listSubtitle && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ color: 'primary.main' }}
                      marginBottom={0.5}>
                      <SubtitleIcon
                        style={{
                          width: 12,
                          height: 12,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="text-sm"
                        color="primary"
                        component="span">
                        {listSubtitle.icon === 'forPerson'
                          ? t('trainings.listSubtitleForUser', {
                              name: listSubtitle.label,
                            })
                          : t('trainings.listSubtitleFromUser', {
                              name: listSubtitle.label,
                            })}
                      </Typography>
                    </Box>
                  )}
                  {trainingDate && (
                    <Typography variant="label-text-md">
                      {trainingDate}
                    </Typography>
                  )}
                </Box>
                {showTrainingActionButtons && (
                  <Box
                    className={classes.buttonsWrapper}
                    onClick={(e) => e.stopPropagation()}>
                    {showStartButton && training?.permissions?.canStart && (
                      <StartActionButton
                        loading={
                          pendingStartId === training.id && isStartingTraining
                        }
                        onClick={() => {
                          if (isShowPlanDialogOnClick?.startTraining) {
                            onStartRestricted?.();
                            return;
                          }
                          setStartTrainingDialogMode('default');
                        }}
                      />
                    )}
                    {showStartButton && training?.permissions?.canFinish && (
                      <FinishActionButton
                        onClick={() =>
                          navigate(Paths.trainings.toFinishing(training.id))
                        }
                      />
                    )}
                    {showCopyButton && training?.permissions?.canCopy && (
                      <CopyActionButton
                        onClick={() => setShowCopyTrainingDialog(true)}
                      />
                    )}
                    {showShareButton && training?.permissions?.canShare && (
                      <ShareActionButton
                        onClick={() => setIsShareTraining(true)}
                      />
                    )}

                    {showEditButton &&
                      ((training.status === TrainingStatus.notStarted &&
                        training?.permissions?.canEdit) ||
                        (training.status !== TrainingStatus.notStarted &&
                          (training?.permissions?.canEdit ||
                            training?.permissions?.canEditInfo))) && (
                        <EditActionButton
                          onClick={() => onEditClick(training)}
                        />
                      )}
                    {showDeleteButton && training?.permissions?.canDelete && (
                      <DeleteActionButton
                        onClick={() => setDeleteConfirm(true)}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                {training?.status === TrainingStatus.started && (
                  <Typography variant="primary-wrapped-sm">
                    {t(`trainings.statusStarted`)}
                  </Typography>
                )}
                {training?.status === TrainingStatus.notStarted && (
                  <Typography variant="grey-wrapped-sm">
                    {t(`trainings.statusNotStarted`)}
                  </Typography>
                )}
                {training?.status === TrainingStatus.finished && (
                  <Typography variant="success-wrapped-sm">
                    {t(`trainings.statusFinished`)}
                  </Typography>
                )}
                {training?.isGenerated && (
                  <Typography variant="primary-wrapped-sm">
                    {t('trainings.aiGenerated')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </div>
        <div className={classes.trainingItemContent}></div>
      </div>

      <ShareTraining
        open={!!isShareTraining}
        training={training}
        onClose={() => setIsShareTraining(false)}
      />

      <AppDialog
        open={showCopyTrainingDialog}
        onClose={() => setShowCopyTrainingDialog(false)}
        title={t('trainings.copyTraining')}>
        <CopyTraining
          training={training}
          onCancel={() => setShowCopyTrainingDialog(false)}
          onCopyTraining={onCopyTraining}
        />
      </AppDialog>

      <ConfirmDialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title={t('common.delete')}
        onSubmit={() =>
          deleteTraining({ id: training.id })
            .unwrap()
            .then(() => {
              setDeleteConfirm(false);
              return onTrainingDeleted?.();
            })
            .catch((error) => {
              console.log(error);
            })
        }
        isLoading={isDeletingTraining}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>

      <ConfirmDialog
        open={startTrainingDialogMode !== null}
        onClose={() => setStartTrainingDialogMode(null)}
        title={t('trainings.startQuestion')}
        onSubmit={() => {
          setPendingStartId(training.id);
          return startTraining(training.id)
            .unwrap()
            .then(() => {
              setStartTrainingDialogMode(null);
              navigate(Paths.trainings.toCurrent(training.id));
            })
            .catch((e) => {
              console.log(e);
            })
            .finally(() => {
              setPendingStartId(null);
            });
        }}
        isLoading={isStartingTraining}>
        {t('trainings.startQuestionMessage')}
      </ConfirmDialog>
    </React.Fragment>
  );
};

export default TrainingListItem;
